import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActiveDialog, LayoutModule, MonPromptService } from '@monsido/angular-shared-components';
import { CustomerRepo } from 'app/services/api/admin/customer-repo';
import { LANGUAGE_CONSTANT } from '@monsido/core/constants/languages.constant';
import { UnsavedUser } from '@monsido/modules/models/api/interfaces/user.interface';
import { HttpHeaders } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { FormUserUploadComponent } from './steps/form-user-upload/form-user-upload.component';
import { FormUserImportSetupComponent, UserImportCSVData, UserImportSelectedModel } from './steps/setup/setup.component';
import { FormUserImportDomainsComponent } from './steps/domains/domains.component';
import { UserImportConfirmComponent } from './steps/confirm/user-import-confirm/user-import-confirm.component';
import { TranslateModule } from 'app/modules/translate.module';
import { TranslateService } from 'app/services/translate/translate.service';
import { BackendAdminCustomersRepo } from '../../services/api/backend_admin/backend-admin-customers-repo';
import { UserImportCsvService } from './services/user-import-csv.service';

@Component({
    selector: 'mon-form-import-users',
    imports: [
        CommonModule,
        FormsModule,
        LayoutModule,
        TranslateModule,
        FormUserUploadComponent,
        FormUserImportSetupComponent,
        FormUserImportDomainsComponent,
        UserImportConfirmComponent,
    ],
    templateUrl: './import-users.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormImportUsersComponent {
    @Input() customerId: number;
    @Input() apiType: 'customer' | 'backendAdmin' = 'customer';
    @ViewChild('setupComponent') setupComponent: FormUserImportSetupComponent;

    step: number = 1;
    saving: boolean = false;
    data: UserImportCSVData;
    selected: UserImportSelectedModel = {};

    constructor (
        private activeDialog: ActiveDialog,
        private translateService: TranslateService,
        private customerRepo: CustomerRepo,
        private promptService: MonPromptService,
        private backendAdminCustomersRepo: BackendAdminCustomersRepo,
        private userImportCsvService: UserImportCsvService,
    ) {}

    private get repo (): BackendAdminCustomersRepo | CustomerRepo {
        return this.apiType === 'backendAdmin' ? this.backendAdminCustomersRepo : this.customerRepo;
    }

    get isCurrentStepValid (): boolean {
        if (this.step === 2 && this.setupComponent) {
            // For step 2, only setup component validation is required
            // Domain selection is optional (hidden for enhanced CSV format)
            return this.setupComponent.isFormValid();
        }
        return true; // For other steps, assume valid or handle as needed
    }

    submit (): void {
        let chain: Promise<unknown> = Promise.resolve();
        const failedUsers: Array<{ user: UnsavedUser, error: string }> = [];

        this.saving = true;


        for (const value of this.data.data.slice(1)) {
            const user: UnsavedUser = {
                email: value[this.selected.emailIndex],
                first_name: value[this.selected.firstNameIndex],
                last_name: value[this.selected.lastNameIndex],
                locale: LANGUAGE_CONSTANT.en.code,
                customer_admin: value[this.selected.isAdminIndex]?.toLowerCase() === 'yes',
            };

            // Handle domain assignments based on CSV format
            if (this.data.format === 'enhanced') {
                // Enhanced CSV: Parse domain IDs from CSV columns
                this.assignDomainsFromCSV(user, value);
            } else {
                // Basic CSV: Use manually selected domains
                this.assignDomainsFromSelection(user);
            }

            const pwd = value[this.selected.passwordIndex];

            if (pwd) {
                user.password = pwd;
                user.password_confirmation = pwd;
            }

            if (user.email) {
                chain = chain.then(() => {

                    return this.repo
                        .createUser(
                            this.customerId,
                            user,
                            new HttpHeaders({
                                noGlobal: 'true',
                            }),
                        )
                        .then(() => {}, (error) => {
                            // Extract error message from API response using service
                            const errorMessage = this.userImportCsvService.extractErrorMessage(error);

                            failedUsers.push({
                                user: user,
                                error: errorMessage,
                            });
                        });
                }, () => {});
            }
        }

        chain
            .then(() => {
                if (failedUsers.length > 0) {
                    // Partial failure - some users couldn't be created
                    const unableToCreate = this.translateService.getString('Unable to create the following users:');
                    const failedUserDetails = failedUsers
                        .map(item => `${item.user.email}: ${item.error}`)
                        .join('\n');
                    const message = `${unableToCreate}\n\n${failedUserDetails}`;

                    // Use large dialog for error reports to show more details
                    this.promptService.alert(message, { size: 'lg' });
                } else {
                    // Complete success
                    const successMessage = this.translateService.getString(
                        'User import completed successfully! All users have been submitted for creation. Please refresh the user list to see the new users.',
                    );
                    this.promptService.alert(successMessage);
                }
            }, (): void => {})
            .finally(() => this.close());
    }

    /**
     * Assign domains to user from manually selected domains (basic CSV format)
     */
    private assignDomainsFromSelection (user: UnsavedUser): void {
        user.domain_users = this.selected.domains?.map(domain => ({
            ...domain,
            visible: this.userImportCsvService.calculateVisibility(user, domain.visible),
            send_report: this.userImportCsvService.calculateSendReport(domain.visible, domain.send_report),
        }));

        user.domain_group_members = this.selected.domainGroups?.map(group => ({
            ...group,
            visible: this.userImportCsvService.calculateVisibility(user, group.visible),
            send_report: this.userImportCsvService.calculateSendReport(group.visible, group.send_report),
        }));
    }

    /**
     * Assign domains to user from CSV column data (enhanced CSV format)
     */
    private assignDomainsFromCSV (user: UnsavedUser, csvRow: string[]): void {
        // Parse domain IDs from CSV if column is specified
        if (this.selected.domainIdsColumnIndex !== undefined && this.selected.domainIdsColumnIndex >= 0) {
            const domainIdsCell = csvRow[this.selected.domainIdsColumnIndex];
            const domainIds = this.userImportCsvService.parseIds(domainIdsCell) || [];

            // Convert domain IDs to domain_users format using service helper
            user.domain_users = domainIds.map(domainId =>
                this.userImportCsvService.createDomainUserAssignment(domainId, user),
            );
        } else {
            user.domain_users = [];
        }

        // Parse domain group IDs from CSV if column is specified
        if (this.selected.domainGroupIdsColumnIndex !== undefined && this.selected.domainGroupIdsColumnIndex >= 0) {
            const domainGroupIdsCell = csvRow[this.selected.domainGroupIdsColumnIndex];
            const domainGroupIds = this.userImportCsvService.parseIds(domainGroupIdsCell) || [];

            // Convert domain group IDs to domain_group_members format using service helper
            user.domain_group_members = domainGroupIds.map(domainGroupId =>
                this.userImportCsvService.createDomainGroupAssignment(domainGroupId, user),
            );
        } else {
            user.domain_group_members = [];
        }
    }

    onDataChange (data: UserImportCSVData): void {
        this.data = data;
    }

    onStepChange (step: number): void {
        this.step = step;
    }

    close (): void {
        this.activeDialog.close();
    }
}
